import { createServerFn } from '@tanstack/react-start'
import { randomBytes } from 'node:crypto'
import { z } from 'zod'
import type { SiteBannerConfig } from '#bv/lib/adminTypes'
import type { ManualFfOrder } from '#bv/lib/adminTypes'
import { readStoreFile, writeStoreFile, pruneSessionsInStore } from '#bv/lib/adminStoreFile'
import { useSupabaseAdminStore } from '#bv/lib/supabaseAdminServer'
import {
  dbAssertToken,
  dbDeleteAdminOrder,
  dbUpdateAdminOrderStatus,
  dbDeleteListingOverride,
  dbDeleteProductCustom,
  dbDeleteSessionToken,
  dbGetBanner,
  dbGetDashboardData,
  dbInsertProductCustom,
  dbListListingOverrides,
  dbListProductCustom,
  dbListProductStockOverrides,
  dbSaveSessionToken,
  dbSetBanner,
  dbSetFeaturedProductIds,
  dbUpdateProductCustom,
  dbUpsertProductStockOverride,
  dbUpsertListingOverride,
} from '#bv/lib/adminStoreSupabase'
import baseCatalog from '#bv/data/products'
import {
  mergeCatalogSync,
  type ProductCustomRow,
  type ProductListingOverrideRow,
} from '#bv/lib/productCatalog'
import {
  deleteCryptoLedgerPayment,
  getCryptoLedgerPayment,
  listCryptoLedgerPaymentsForAdmin,
  updateCryptoLedgerPayment,
} from '#bv/lib/cryptoPaymentLedger'
import { fetchBlackVaultRollup } from '#bv/lib/revenueCrossSite'
import { utcLastFullMonthRange, utcThisMonthToDateRange } from '#bv/lib/revenueDateRanges'
import { mergeUnifiedDashboardOrders } from '#bv/lib/adminOrdersUnified'
import { getAdminOrderById } from '#bv/lib/adminOrderLookup'
import { listManualFfOrders, markManualFfOrderRefunded, setManualFfOrderStatus } from '#bv/lib/manualFf'
import { deleteManualFfOrderStore } from '#bv/lib/manualFfStore'
import { disputePdfResponseToRpcPayload } from '#bv/lib/disputePackRpc'
import { adminExportDisputeFromJson } from '#bv/lib/httpAdminExportDispute'
import {
  accountsPlanetGetUser,
  accountsPlanetListProducts,
  accountsPlanetPurchase,
  accountsPlanetTestPurchasesAllowed,
  getAccountsPlanetConfig,
} from '#bv/lib/accountsPlanetApi'

const SESSION_MS = 1000 * 60 * 60 * 48

type SupabaseAuthUserJson = {
  email?: string | null
  app_metadata?: { provider?: string; providers?: string[] }
  user_metadata?: {
    user_name?: string
    preferred_username?: string
    /** GitHub OAuth often stores login here */
    nickname?: string
  }
  identities?: Array<{
    provider?: string
    identity_data?: Record<string, unknown>
  }>
}

function pickGithubLoginFromIdentityData(data: Record<string, unknown> | undefined): string {
  if (!data) return ''
  const candidates = [
    data.preferred_username,
    data.user_name,
    data.nickname,
    data.login,
    data.slug,
  ]
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim()) return c.trim().toLowerCase()
  }
  return ''
}

function githubLoginFromSupabaseUser(user: SupabaseAuthUserJson): string {
  const fromMeta =
    user.user_metadata?.user_name ??
    user.user_metadata?.preferred_username ??
    user.user_metadata?.nickname
  if (fromMeta && String(fromMeta).trim()) return String(fromMeta).trim().toLowerCase()
  const gh = user.identities?.find((i) => i.provider === 'github')
  const id = pickGithubLoginFromIdentityData(gh?.identity_data as Record<string, unknown> | undefined)
  return id || ''
}

async function verifyGithubOrgWithUserToken(providerToken: string, org: string): Promise<void> {
  const orgRsp = await fetch(`https://api.github.com/user/memberships/orgs/${encodeURIComponent(org)}`, {
    headers: {
      Authorization: `Bearer ${providerToken}`,
      Accept: 'application/vnd.github+json',
      'X-GitHub-Api-Version': '2022-11-28',
    },
  })
  if (!orgRsp.ok) {
    throw new Error(`GitHub org check failed (${orgRsp.status})`)
  }
  const membership = (await orgRsp.json()) as { state?: string }
  if (membership.state !== 'active') {
    throw new Error(`GitHub org membership for "${org}" must be active`)
  }
}

/** PAT from an org owner/admin with permission to confirm membership (classic: read:org + repo or admin; fine-grained: Members read). */
async function verifyGithubOrgWithServicePat(pat: string, org: string, githubUsername: string): Promise<void> {
  const user = githubUsername.trim().toLowerCase()
  if (!user) {
    throw new Error('GitHub username missing from Supabase profile; cannot verify organization.')
  }
  const res = await fetch(
    `https://api.github.com/orgs/${encodeURIComponent(org)}/members/${encodeURIComponent(user)}`,
    {
      headers: {
        Authorization: `Bearer ${pat}`,
        Accept: 'application/vnd.github+json',
        'X-GitHub-Api-Version': '2022-11-28',
      },
    },
  )
  if (res.status === 204) return
  if (res.status === 404) {
    throw new Error(`GitHub user "${user}" is not an active member of organization "${org}".`)
  }
  const hint = await res.text()
  throw new Error(
    `GitHub org membership check failed (${res.status}). Check ADMIN_GITHUB_ORG_SERVICE_TOKEN permissions. ${hint.slice(0, 160)}`,
  )
}

async function assertToken(token: string | undefined): Promise<void> {
  if (!token || typeof token !== 'string') {
    throw new Error('Not authenticated')
  }
  if (useSupabaseAdminStore()) {
    await dbAssertToken(token)
    return
  }
  const s = readStoreFile()
  pruneSessionsInStore(s)
  const exp = s.sessions[token]
  if (!exp || exp < Date.now()) {
    throw new Error('Session expired')
  }
}

export const getPublicBanner = createServerFn({ method: 'GET' }).handler(async (): Promise<SiteBannerConfig> => {
  if (useSupabaseAdminStore()) {
    return dbGetBanner()
  }
  return readStoreFile().banner
})

const githubLoginSchema = z.object({
  accessToken: z.string().min(20),
  /** Supabase sometimes sends ""; treat as absent so Zod does not reject the whole payload. */
  githubProviderToken: z
    .union([z.string().min(20), z.literal('')])
    .optional()
    .transform((v) => (typeof v === 'string' && v.length >= 20 ? v : undefined)),
})

export const adminLoginWithGithub = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => githubLoginSchema.parse(d))
  .handler(async ({ data }) => {
    const supabaseUrl = process.env.SUPABASE_URL
    const supabaseAnonKey = process.env.SUPABASE_ANON_KEY
    const allowlistRaw = process.env.ADMIN_GITHUB_ALLOWLIST
    if (!supabaseUrl || !supabaseAnonKey) {
      throw new Error('SUPABASE_URL and SUPABASE_ANON_KEY are required.')
    }
    const allowlist = (allowlistRaw ?? '')
      .split(',')
      .map((v) => v.trim().toLowerCase())
      .filter(Boolean)
    const requiredOrg = process.env.ADMIN_GITHUB_ORG?.trim().toLowerCase()
    if (!requiredOrg && allowlist.length === 0) {
      throw new Error('Set ADMIN_GITHUB_ORG or ADMIN_GITHUB_ALLOWLIST for admin access control.')
    }

    const rsp = await fetch(`${supabaseUrl}/auth/v1/user`, {
      headers: {
        Authorization: `Bearer ${data.accessToken}`,
        apikey: supabaseAnonKey,
      },
    })
    if (!rsp.ok) {
      throw new Error('GitHub session validation failed')
    }
    const user = (await rsp.json()) as SupabaseAuthUserJson
    const provider = user.app_metadata?.provider ?? user.app_metadata?.providers?.[0]
    if (provider !== 'github') {
      throw new Error('GitHub login is required')
    }
    const email = user.email?.toLowerCase() ?? ''
    const githubLogin = githubLoginFromSupabaseUser(user)
    if (
      allowlist.length > 0 &&
      !allowlist.includes(email) &&
      (!githubLogin || !allowlist.includes(githubLogin))
    ) {
      throw new Error('This GitHub account is not approved for admin access')
    }
    if (requiredOrg) {
      const servicePat = process.env.ADMIN_GITHUB_ORG_SERVICE_TOKEN?.trim()
      // Prefer server PAT when configured — Supabase restored sessions rarely include provider_token,
      // and an old provider_token can 401 while PAT keeps login reliable.
      if (servicePat) {
        await verifyGithubOrgWithServicePat(servicePat, requiredOrg, githubLogin)
      } else if (data.githubProviderToken) {
        await verifyGithubOrgWithUserToken(data.githubProviderToken, requiredOrg)
      } else {
        throw new Error(
          'GitHub provider token missing. Reconnect GitHub login in Supabase, or set ADMIN_GITHUB_ORG_SERVICE_TOKEN for server-side org verification.',
        )
      }
    }

    const token = randomBytes(24).toString('hex')
    const expiresAt = Date.now() + SESSION_MS
    if (useSupabaseAdminStore()) {
      await dbSaveSessionToken(token, expiresAt)
    } else {
      const s = readStoreFile()
      pruneSessionsInStore(s)
      s.sessions[token] = expiresAt
      writeStoreFile(s)
    }
    return { token }
  })

const tokenSchema = z.object({ token: z.string().min(16) })

export const adminLogout = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => tokenSchema.parse(d))
  .handler(async ({ data }) => {
    if (useSupabaseAdminStore()) {
      await dbDeleteSessionToken(data.token)
    } else {
      const s = readStoreFile()
      delete s.sessions[data.token]
      writeStoreFile(s)
    }
    return { ok: true as const }
  })

export const adminPing = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => tokenSchema.parse(d))
  .handler(async ({ data }) => {
    await assertToken(data.token)
    return { ok: true as const }
  })

export const adminGetDashboard = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => tokenSchema.parse(d))
  .handler(async ({ data }) => {
    await assertToken(data.token)
    const base = useSupabaseAdminStore()
      ? await dbGetDashboardData()
      : (() => {
          const s = readStoreFile()
          return {
            customers: s.customers,
            orders: s.orders,
            banner: s.banner,
            featuredProductIds: s.featuredProductIds ?? [],
          }
        })()
    const [ffList, cryptoList] = await Promise.all([
      listManualFfOrders().catch(() => [] as ManualFfOrder[]),
      listCryptoLedgerPaymentsForAdmin().catch(() => []),
    ])
    const orders = mergeUnifiedDashboardOrders(base.orders, ffList, cryptoList)
    const { normalizeFeaturedProductIds } = await import('#bv/data/productFamilies')
    return {
      ...base,
      orders,
      featuredProductIds: normalizeFeaturedProductIds(base.featuredProductIds ?? []),
    }
  })

const shopRevenueMonthsSchema = z.object({
  token: z.string().min(16),
})

export type AdminShopRevenueMonth = {
  totalUsd: number
  moneymotionUsd: number
  manualFfUsd: number
  cryptoUsd: number
  rows: number
  label: string
  rangeStart: string
  rangeEnd: string
}

/** Same BlackVault shop totals as /internal/revenue (UTC MTD + prior full UTC month). */
export const adminGetShopRevenueMonths = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => shopRevenueMonthsSchema.parse(d))
  .handler(async ({ data }): Promise<{ thisMonth: AdminShopRevenueMonth; lastMonth: AdminShopRevenueMonth }> => {
    await assertToken(data.token)
    const cur = utcThisMonthToDateRange()
    const last = utcLastFullMonthRange()
    const [thisRollup, lastRollup] = await Promise.all([
      fetchBlackVaultRollup({ start: cur.startIso, end: cur.endIso }),
      fetchBlackVaultRollup({ start: last.startIso, end: last.endIso }),
    ])
    const mapRollup = (
      rollup: Awaited<ReturnType<typeof fetchBlackVaultRollup>>,
      range: { startIso: string; endIso: string; label: string },
    ): AdminShopRevenueMonth => ({
      totalUsd: rollup.usd,
      moneymotionUsd: rollup.moneymotion_usd,
      manualFfUsd: rollup.manual_ff_usd,
      cryptoUsd: rollup.crypto_usd,
      rows: rollup.rows,
      label: range.label,
      rangeStart: range.startIso,
      rangeEnd: range.endIso,
    })
    return {
      thisMonth: mapRollup(thisRollup, cur),
      lastMonth: mapRollup(lastRollup, last),
    }
  })

const updateBannerSchema = z.object({
  token: z.string().min(16),
  banner: z.object({
    enabled: z.boolean(),
    items: z.array(z.object({ icon: z.string(), text: z.string().min(1) })).max(20),
  }),
})

export const adminUpdateBanner = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => updateBannerSchema.parse(d))
  .handler(async ({ data }) => {
    await assertToken(data.token)
    const next: SiteBannerConfig = {
      enabled: data.banner.enabled,
      items: data.banner.items as SiteBannerConfig['items'],
    }
    if (useSupabaseAdminStore()) {
      await dbSetBanner(next)
      return { ok: true as const, banner: next }
    }
    const s = readStoreFile()
    s.banner = next
    writeStoreFile(s)
    return { ok: true as const, banner: s.banner }
  })

const updateFeaturedSchema = z.object({
  token: z.string().min(16),
  productIds: z.array(z.number().int().positive()).max(12),
})

export const adminUpdateFeatured = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => updateFeaturedSchema.parse(d))
  .handler(async ({ data }) => {
    await assertToken(data.token)
    const { normalizeFeaturedProductIds } = await import('#bv/data/productFamilies')
    const productIds = normalizeFeaturedProductIds(data.productIds)
    if (useSupabaseAdminStore()) {
      await dbSetFeaturedProductIds(productIds)
      return { ok: true as const, featuredProductIds: productIds }
    }
    const s = readStoreFile()
    s.featuredProductIds = productIds
    writeStoreFile(s)
    return { ok: true as const, featuredProductIds: s.featuredProductIds }
  })

export const adminGetProductCatalog = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => tokenSchema.parse(d))
  .handler(
    async ({
      data,
    }): Promise<{
      overrides: ProductListingOverrideRow[]
      custom: ProductCustomRow[]
      merged: import('#bv/data/products').Product[]
      stockOverrides: Record<number, boolean>
      supabaseCatalog: boolean
    }> => {
      await assertToken(data.token)
      if (!useSupabaseAdminStore()) {
        return {
          overrides: [],
          custom: [],
          merged: baseCatalog.map((p) => ({ ...p })),
          stockOverrides: {},
          supabaseCatalog: false,
        }
      }
      const [overrides, custom, stockRows] = await Promise.all([
        dbListListingOverrides(),
        dbListProductCustom(),
        dbListProductStockOverrides(),
      ])
      const merged = mergeCatalogSync(
        baseCatalog.map((p) => ({ ...p })),
        overrides,
        custom,
      )
      const stockOverrides: Record<number, boolean> = {}
      for (const row of stockRows) stockOverrides[row.product_id] = !!row.in_stock
      return { overrides, custom, merged, stockOverrides, supabaseCatalog: true }
    },
  )

const upsertListingSchema = z.object({
  token: z.string().min(16),
  productId: z.number().int().positive(),
  displayName: z.string().trim().max(200).nullable().optional(),
  priceUsd: z.number().nonnegative().nullable().optional(),
  compareAtUsd: z.number().nonnegative().nullable().optional(),
  deliveryInfo: z.string().trim().max(2000).nullable().optional(),
  hidden: z.boolean(),
})

export const adminUpsertListingOverride = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => upsertListingSchema.parse(d))
  .handler(async ({ data }) => {
    await assertToken(data.token)
    if (!useSupabaseAdminStore()) {
      throw new Error('Catalog edits require Supabase (SUPABASE_URL + SUPABASE_SERVICE_ROLE_KEY).')
    }
    await dbUpsertListingOverride({
      product_id: data.productId,
      display_name: data.displayName ?? null,
      price_usd: data.priceUsd ?? null,
      compare_at_usd: data.compareAtUsd ?? null,
      delivery_info: data.deliveryInfo ?? null,
      hidden: data.hidden,
    })
    return { ok: true as const }
  })

const deleteListingSchema = z.object({
  token: z.string().min(16),
  productId: z.number().int().positive(),
})

export const adminDeleteListingOverride = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => deleteListingSchema.parse(d))
  .handler(async ({ data }) => {
    await assertToken(data.token)
    if (!useSupabaseAdminStore()) {
      throw new Error('Catalog edits require Supabase.')
    }
    await dbDeleteListingOverride(data.productId)
    return { ok: true as const }
  })

const insertCustomSchema = z.object({
  token: z.string().min(16),
  name: z.string().trim().min(1).max(200),
  price: z.number().nonnegative(),
  compareAtUsd: z.number().nonnegative().nullable().optional(),
  category: z.enum([
    'discord',
    'streaming',
    'gaming',
    'social',
    'software',
    'zenith_lifetime',
    'zenith_monthly',
    'alchemy_lifetime',
    'alchemy_token',
    'alchemy_30_days',
  ]),
  image: z.string().trim().max(300).optional(),
  shortDescription: z.string().trim().max(500).default(''),
  hidden: z.boolean().optional().default(false),
  /** AccountsPlanet product uniqid (GET /api/products/{uniqid}); optional for manual test buys. */
  accountsplanetUniqid: z.string().trim().max(120).optional(),
})

export const adminInsertProductCustom = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => insertCustomSchema.parse(d))
  .handler(async ({ data }) => {
    await assertToken(data.token)
    if (!useSupabaseAdminStore()) {
      throw new Error('Catalog edits require Supabase.')
    }
    const r = await dbInsertProductCustom({
      name: data.name,
      price: data.price,
      compare_at_usd: data.compareAtUsd ?? null,
      category: data.category,
      image: data.image || '/placeholder.png',
      short_description: data.shortDescription,
      hidden: data.hidden ?? false,
      accountsplanet_uniqid: data.accountsplanetUniqid?.trim() || null,
    })
    return { ok: true as const, syntheticId: r.synthetic_id }
  })

const updateCustomSchema = z.object({
  token: z.string().min(16),
  syntheticId: z.number().int().positive(),
  name: z.string().trim().min(1).max(200).optional(),
  price: z.number().nonnegative().optional(),
  compareAtUsd: z.number().nonnegative().nullable().optional(),
  category: z
    .enum([
      'discord',
      'streaming',
      'gaming',
      'social',
      'software',
      'zenith_lifetime',
      'zenith_monthly',
      'alchemy_lifetime',
      'alchemy_token',
      'alchemy_30_days',
    ])
    .optional(),
  image: z.string().trim().max(300).optional(),
  shortDescription: z.string().trim().max(500).optional(),
  hidden: z.boolean().optional(),
  accountsplanetUniqid: z.union([z.string().trim().max(120), z.null()]).optional(),
})

export const adminUpdateProductCustom = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => updateCustomSchema.parse(d))
  .handler(async ({ data }) => {
    await assertToken(data.token)
    if (!useSupabaseAdminStore()) {
      throw new Error('Catalog edits require Supabase.')
    }
    const patch: Parameters<typeof dbUpdateProductCustom>[1] = {}
    if (data.name != null) patch.name = data.name
    if (data.price != null) patch.price = data.price
    if (data.compareAtUsd !== undefined) patch.compare_at_usd = data.compareAtUsd
    if (data.category != null) patch.category = data.category
    if (data.image != null) patch.image = data.image
    if (data.shortDescription != null) patch.short_description = data.shortDescription
    if (data.hidden != null) patch.hidden = data.hidden
    if (data.accountsplanetUniqid !== undefined) {
      patch.accountsplanet_uniqid =
        data.accountsplanetUniqid === null ? null : data.accountsplanetUniqid.trim() || null
    }
    await dbUpdateProductCustom(data.syntheticId, patch)
    return { ok: true as const }
  })

const apBrowseSchema = z.object({
  token: z.string().min(16),
  perPage: z.number().int().min(1).max(50).optional(),
  search: z.string().trim().max(100).optional(),
})

export const adminAccountsPlanetStatus = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => tokenSchema.parse(d))
  .handler(async ({ data }) => {
    await assertToken(data.token)
    return {
      tokenConfigured: Boolean(getAccountsPlanetConfig()),
      testPurchasesAllowed: accountsPlanetTestPurchasesAllowed(),
    }
  })

export const adminAccountsPlanetPing = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => tokenSchema.parse(d))
  .handler(async ({ data }) => {
    await assertToken(data.token)
    const r = await accountsPlanetGetUser()
    if (!r.ok) return { ok: false as const, status: r.status, error: r.text }
    return { ok: true as const, body: r.json }
  })

export const adminAccountsPlanetBrowseProducts = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => apBrowseSchema.parse(d))
  .handler(async ({ data }) => {
    await assertToken(data.token)
    const r = await accountsPlanetListProducts({
      page: 1,
      perPage: data.perPage ?? 10,
      search: data.search,
      inStockOnly: false,
    })
    if (!r.ok) return { ok: false as const, status: r.status, error: r.text }
    return { ok: true as const, body: r.json }
  })

const apTestPurchaseSchema = z.object({
  token: z.string().min(16),
  productUniqid: z.string().trim().min(2).max(120),
  qty: z.number().int().min(1).max(5).optional(),
})

export const adminAccountsPlanetTestPurchase = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => apTestPurchaseSchema.parse(d))
  .handler(async ({ data }) => {
    await assertToken(data.token)
    if (!accountsPlanetTestPurchasesAllowed()) {
      throw new Error(
        'Test purchases are off. Set ACCOUNTSPLANET_ALLOW_TEST_PURCHASES=1 on the server (uses real AP balance), redeploy, then retry.',
      )
    }
    const r = await accountsPlanetPurchase({
      product_id: data.productUniqid.trim(),
      qty: data.qty ?? 1,
    })
    if (!r.ok) return { ok: false as const, status: r.status, error: r.text }
    return { ok: true as const, body: r.json }
  })

const deleteCustomSchema = z.object({
  token: z.string().min(16),
  syntheticId: z.number().int().positive(),
})

export const adminDeleteProductCustom = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => deleteCustomSchema.parse(d))
  .handler(async ({ data }) => {
    await assertToken(data.token)
    if (!useSupabaseAdminStore()) {
      throw new Error('Catalog edits require Supabase.')
    }
    await dbDeleteProductCustom(data.syntheticId)
    return { ok: true as const }
  })

const setProductStockSchema = z.object({
  token: z.string().min(16),
  productId: z.number().int().positive(),
  inStock: z.boolean(),
})

export const adminSetProductStock = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => setProductStockSchema.parse(d))
  .handler(async ({ data }) => {
    await assertToken(data.token)
    if (!useSupabaseAdminStore()) {
      throw new Error('Stock toggles require Supabase.')
    }
    await dbUpsertProductStockOverride({
      product_id: data.productId,
      in_stock: data.inStock,
    })
    return { ok: true as const }
  })

const getManualOrdersSchema = z.object({
  token: z.string().min(16),
})

export const adminGetManualFfOrders = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => getManualOrdersSchema.parse(d))
  .handler(async ({ data }): Promise<{ orders: ManualFfOrder[] }> => {
    await assertToken(data.token)
    return { orders: await listManualFfOrders() }
  })

const updateManualOrderStatusSchema = z.object({
  token: z.string().min(16),
  orderId: z.string().min(5),
  status: z.enum(['verifying_payment', 'paid', 'delivered', 'cancelled']),
  note: z.string().trim().max(300).optional(),
})

export const adminUpdateManualFfOrderStatus = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => updateManualOrderStatusSchema.parse(d))
  .handler(async ({ data }): Promise<{ order: ManualFfOrder }> => {
    await assertToken(data.token)
    const order = await setManualFfOrderStatus({
      orderId: data.orderId,
      status: data.status,
      note: data.note,
    })
    return { order }
  })

const exportDisputePackSchema = z.object({
  token: z.string().min(10),
  orderId: z.string().min(2).max(200),
})

export const adminExportDisputePack = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => exportDisputePackSchema.parse(d))
  .handler(async ({ data }) => {
    const res = await adminExportDisputeFromJson({ token: data.token, orderId: data.orderId })
    return disputePdfResponseToRpcPayload(res)
  })

const deleteAdminOrderSchema = z.object({
  token: z.string().min(16),
  orderId: z.string().min(2).max(200),
})

const refundAdminOrderSchema = z.object({
  token: z.string().min(16),
  orderId: z.string().min(2).max(200),
  note: z.string().trim().max(300).optional(),
})

export const adminRefundOrder = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => refundAdminOrderSchema.parse(d))
  .handler(async ({ data }) => {
    await assertToken(data.token)
    const id = data.orderId.trim()
    if (!id) throw new Error('Order id required.')

    if (id.startsWith('ff_')) {
      const order = await markManualFfOrderRefunded(id, data.note)
      return { ok: true as const, order }
    }

    const cryptoRow = await getCryptoLedgerPayment(id)
    if (cryptoRow) {
      if (cryptoRow.paymentStatus === 'refunded') return { ok: true as const, alreadyRefunded: true }
      if (cryptoRow.paymentStatus !== 'finished') {
        throw new Error('Only paid crypto orders can be marked refunded.')
      }
      await updateCryptoLedgerPayment({
        ...cryptoRow,
        paymentStatus: 'refunded',
        updatedAt: new Date().toISOString(),
      })
      return { ok: true as const }
    }

    const mm = await getAdminOrderById(id)
    if (mm) {
      if (mm.status === 'refunded') return { ok: true as const, alreadyRefunded: true }
      if (mm.status !== 'paid') throw new Error('Only paid orders can be marked refunded.')
      if (useSupabaseAdminStore()) {
        await dbUpdateAdminOrderStatus(id, 'refunded')
      } else {
        const s = readStoreFile()
        const idx = s.orders.findIndex((o) => o.id === id)
        if (idx === -1) throw new Error('Order not found.')
        s.orders[idx] = { ...s.orders[idx], status: 'refunded' }
        writeStoreFile(s)
      }
      return { ok: true as const }
    }

    throw new Error('Order not found.')
  })

export const adminDeleteOrder = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => deleteAdminOrderSchema.parse(d))
  .handler(async ({ data }) => {
    await assertToken(data.token)
    const id = data.orderId.trim()
    if (!id) throw new Error('Order id required.')
    if (id.startsWith('ff_')) {
      await deleteManualFfOrderStore(id)
      return { ok: true as const }
    }
    const mm = await getAdminOrderById(id)
    if (mm) {
      if (useSupabaseAdminStore()) {
        await dbDeleteAdminOrder(id)
      } else {
        const s = readStoreFile()
        const idx = s.orders.findIndex((o) => o.id === id)
        if (idx === -1) throw new Error('Order not found.')
        s.orders.splice(idx, 1)
        writeStoreFile(s)
      }
      return { ok: true as const }
    }
    const cryptoRow = await getCryptoLedgerPayment(id)
    if (cryptoRow) {
      await deleteCryptoLedgerPayment(id)
      return { ok: true as const }
    }
    throw new Error('Order not found.')
  })

const confirmCryptoPaymentSchema = z.object({
  token: z.string().min(16),
  paymentId: z.string().min(2).max(200),
})

export const adminConfirmCryptoPayment = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => confirmCryptoPaymentSchema.parse(d))
  .handler(async ({ data }) => {
    await assertToken(data.token)
    const id = data.paymentId.trim()
    if (!id) throw new Error('Payment id required.')
    const row = await getCryptoLedgerPayment(id)
    if (!row) throw new Error('Crypto payment not found.')
    if (row.paymentStatus === 'finished') return { ok: true as const, alreadyFinished: true }
    const next = {
      ...row,
      paymentStatus: 'finished',
      notifiedFinished: true,
      updatedAt: new Date().toISOString(),
    }
    await updateCryptoLedgerPayment(next)
    const { fulfillSmmFromCryptoPayment } = await import('#bv/lib/smmCryptoFulfillment')
    await fulfillSmmFromCryptoPayment(next)
    return { ok: true as const, alreadyFinished: false }
  })

const adminTokenSchema = z.object({ token: z.string().min(16) })

const adminBackfillMmSchema = z.object({
  token: z.string().min(16),
  checkoutSessionIds: z.array(z.string().trim().min(8).max(120)).min(1).max(25),
})

/** Fetch MM checkout sessions and upsert into admin_orders (missed webhooks). */
export const adminBackfillMmCheckoutSessions = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => adminBackfillMmSchema.parse(d))
  .handler(async ({ data }) => {
    await assertToken(data.token)
    const { backfillMoneyMotionCheckoutSessions } = await import('#bv/lib/adminMmBackfill')
    const results = await backfillMoneyMotionCheckoutSessions(data.checkoutSessionIds)
    const failed = results.filter((r) => !r.ok)
    if (failed.length === results.length) {
      throw new Error(failed[0]?.error ?? 'Backfill failed for all sessions.')
    }
    return { results }
  })

export const adminListSmmOrders = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => adminTokenSchema.parse(d))
  .handler(async ({ data }) => {
    await assertToken(data.token)
    const { listSmmOrdersForAdmin } = await import('#bv/lib/smmOrders')
    return { orders: await listSmmOrdersForAdmin(150) }
  })

const smmOrderIdSchema = z.object({ token: z.string().min(16), orderId: z.string().min(4) })

export const adminRefreshSmmOrder = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => smmOrderIdSchema.parse(d))
  .handler(async ({ data }) => {
    await assertToken(data.token)
    const { refreshSmmOrderStatusFromSupplier } = await import('#bv/lib/smmOrders')
    const order = await refreshSmmOrderStatusFromSupplier(data.orderId.trim())
    return { order }
  })

const adminMarkSmmCanceledSchema = z.object({
  token: z.string().min(16),
  orderId: z.string().min(4),
  note: z.string().trim().max(500).optional(),
})

/** Mark order canceled locally (e.g. supplier order cancelled on panel; legacy MTP IDs won't refresh on Raja). */
export const adminMarkSmmOrderCanceled = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => adminMarkSmmCanceledSchema.parse(d))
  .handler(async ({ data }) => {
    await assertToken(data.token)
    const { getSmmOrderById, updateSmmOrder } = await import('#bv/lib/smmOrders')
    const order = await getSmmOrderById(data.orderId.trim())
    if (!order) throw new Error('SMM order not found.')
    const note = data.note?.trim() || 'Marked canceled in admin (supplier order cancelled or legacy panel).'
    await updateSmmOrder(data.orderId.trim(), {
      status: 'Canceled',
      supplier_status_raw: note,
    })
    const updated = await getSmmOrderById(data.orderId.trim())
    if (!updated) throw new Error('SMM order not found after update.')
    return { order: updated }
  })

export const adminRefillSmmOrder = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => smmOrderIdSchema.parse(d))
  .handler(async ({ data }) => {
    await assertToken(data.token)
    const { requestSmmRefill } = await import('#bv/lib/smmOrders')
    const order = await requestSmmRefill(data.orderId.trim())
    return { order }
  })

export const adminSubmitSmmOrder = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => smmOrderIdSchema.parse(d))
  .handler(async ({ data }) => {
    await assertToken(data.token)
    const { submitSmmOrderToSupplier } = await import('#bv/lib/smmOrders')
    const order = await submitSmmOrderToSupplier(data.orderId.trim())
    return { order }
  })

const smmPackagesSchema = z.object({
  token: z.string().min(16),
  productId: z.number().int().positive(),
  enabled: z.boolean(),
  packages: z.array(
    z.object({
      label: z.string().min(1).max(120),
      quantity: z.number().int().positive(),
      price: z.number().positive(),
    }),
  ),
})

export const adminGetSmmPackages = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) =>
    z.object({ token: z.string().min(16), productId: z.number().int().positive() }).parse(d),
  )
  .handler(async ({ data }) => {
    await assertToken(data.token)
    const { getServiceSupabase } = await import('#bv/lib/supabaseAdminServer')
    const sb = getServiceSupabase()
    const base = baseCatalog.find((p) => p.id === data.productId)
    const defaults = base?.smmConfig?.packages ?? []
    if (!sb) return { productId: data.productId, defaults, overrides: null, enabled: true }
    const { data: row } = await sb
      .from('product_smm_packages')
      .select('packages, enabled')
      .eq('product_id', data.productId)
      .maybeSingle()
    const enabled = row == null ? true : row.enabled !== false
    const overrides =
      row && enabled && Array.isArray(row.packages) && row.packages.length > 0
        ? (row.packages as { label: string; quantity: number; price: number }[])
        : null
    return { productId: data.productId, defaults, overrides, enabled }
  })

export const adminSetSmmPackages = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => smmPackagesSchema.parse(d))
  .handler(async ({ data }) => {
    await assertToken(data.token)
    const { setSmmPackageOverrides } = await import('#bv/lib/smmOrders')
    await setSmmPackageOverrides(data.productId, data.packages, data.enabled)
    return { ok: true as const }
  })

export const adminLookupCryptoForSmm = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) =>
    z.object({ token: z.string().min(16), paymentId: z.string().trim().min(4).max(120) }).parse(d),
  )
  .handler(async ({ data }) => {
    await assertToken(data.token)
    const { getCryptoLedgerPayment } = await import('#bv/lib/cryptoPaymentLedger')
    const row = await getCryptoLedgerPayment(data.paymentId.trim())
    if (!row) return { found: false as const }
    return {
      found: true as const,
      buyerEmail: row.buyerEmail,
      totalUsd: row.totalUsd,
      paymentStatus: row.paymentStatus,
      items: row.items,
    }
  })

const adminCreateSmmOrderSchema = z.object({
  token: z.string().min(16),
  productId: z.number().int().positive().optional(),
  checkoutSessionId: z.string().trim().min(4).max(120),
  tiktokTarget: z.string().trim().min(2).max(500),
  packageQuantity: z.number().int().positive(),
  buyerEmail: z.string().trim().email().max(254).optional(),
  amountCents: z.number().int().nonnegative().optional(),
  supplierOrderId: z.string().trim().max(64).optional(),
  /** When true (default if no supplier id), call the SMM panel after insert. */
  submitToPanel: z.boolean().optional(),
})

export const adminCreateSmmOrder = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => adminCreateSmmOrderSchema.parse(d))
  .handler(async ({ data }) => {
    await assertToken(data.token)
    const { normalizeSmmTargetForProduct, smmTargetValidationMessageForProduct } = await import('#bv/lib/smmTarget')
    const checkoutSessionId = data.checkoutSessionId.trim()

    const { getMergedProductById } = await import('#bv/lib/productCatalog')
    const { TIKTOK_REAL_FOLLOWERS_PRODUCT_ID } = await import('#bv/lib/smmProduct')
    const productId = data.productId ?? TIKTOK_REAL_FOLLOWERS_PRODUCT_ID
    const product = await getMergedProductById(productId)
    if (!product?.smmConfig) throw new Error('SMM product not configured.')
    const targetErr = smmTargetValidationMessageForProduct(data.tiktokTarget, product.smmConfig)
    if (targetErr) throw new Error(targetErr)
    const tiktokTarget = normalizeSmmTargetForProduct(data.tiktokTarget, product.smmConfig)!
    const pkg = product.smmConfig.packages.find((p) => p.quantity === data.packageQuantity)
    if (!pkg) throw new Error('Unknown package quantity for this product.')

    let buyerEmail = data.buyerEmail?.trim() ?? ''
    let amountCents = data.amountCents
    if (!buyerEmail || amountCents == null) {
      const { getCryptoLedgerPayment } = await import('#bv/lib/cryptoPaymentLedger')
      const crypto = await getCryptoLedgerPayment(checkoutSessionId)
      if (crypto) {
        if (!buyerEmail) buyerEmail = crypto.buyerEmail
        if (amountCents == null) amountCents = Math.round(crypto.totalUsd * 100)
      }
    }
    if (!buyerEmail) throw new Error('Buyer email is required (or use a known crypto payment id).')
    if (amountCents == null) amountCents = Math.round(pkg.price * 100)

    const {
      getSmmOrderByCheckoutSession,
      insertSmmOrder,
      makeSmmOrderId,
      submitSmmOrderToSupplier,
      updateSmmOrder,
    } = await import('#bv/lib/smmOrders')

    const existing = await getSmmOrderByCheckoutSession(checkoutSessionId)
    if (existing) throw new Error(`SMM order already exists for this checkout: ${existing.id}`)

    const supplierOrderId = data.supplierOrderId?.trim() || null
    const shouldSubmit = data.submitToPanel ?? !supplierOrderId

    const id = makeSmmOrderId()
    await insertSmmOrder({
      id,
      checkout_session_id: checkoutSessionId,
      product_id: product.id,
      product_name: product.name,
      buyer_email: buyerEmail,
      tiktok_target: tiktokTarget,
      package_label: pkg.label,
      quantity: pkg.quantity,
      amount_cents: amountCents,
      currency: 'usd',
      supplier: product.smmConfig.supplier,
      supplier_service_id: product.smmConfig.serviceId,
      supplier_order_id: supplierOrderId,
      status: supplierOrderId ? 'Processing' : 'Pending',
      supplier_status_raw: supplierOrderId ? 'Logged manually' : null,
      enabled: true,
    })

    if (supplierOrderId) {
      return { orderId: id, supplierOrderId, submitted: false as const, logged: true as const }
    }
    if (shouldSubmit) {
      const submitted = await submitSmmOrderToSupplier(id)
      return {
        orderId: id,
        supplierOrderId: submitted.supplier_order_id ?? undefined,
        submitted: true as const,
        logged: false as const,
      }
    }
    return { orderId: id, submitted: false as const, logged: false as const }
  })

const adminAttachSmmSupplierSchema = z.object({
  token: z.string().min(16),
  orderId: z.string().min(4),
  supplierOrderId: z.string().trim().min(1).max(64),
})

export const adminAttachSmmSupplierOrder = createServerFn({ method: 'POST' })
  .inputValidator((d: unknown) => adminAttachSmmSupplierSchema.parse(d))
  .handler(async ({ data }) => {
    await assertToken(data.token)
    const { updateSmmOrder, getSmmOrderById } = await import('#bv/lib/smmOrders')
    const order = await getSmmOrderById(data.orderId.trim())
    if (!order) throw new Error('SMM order not found.')
    await updateSmmOrder(data.orderId.trim(), {
      supplier_order_id: data.supplierOrderId.trim(),
      status: 'Processing',
      supplier_status_raw: 'Supplier id attached in admin',
    })
    const updated = await getSmmOrderById(data.orderId.trim())
    return { order: updated }
  })
